import type * as React from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/primitives/Dropdown";
import { type Theme, useTheme } from "@/context/ThemeContext";
import { useThemePreview } from "@/hooks/useThemePreview";
import { transitions } from "@/styles/transitions";
import { cn } from "@/utils/cn";
import {
  themeColors,
  themeComponentStyles,
  themeData,
  themeSchemaUtils,
} from "@/utils/themeUtils";

/**
 * Theme option interface
 */
interface ThemeOption {
  id: Theme;
  label: string;
}

interface ThemeSelectProps {
  /** Additional CSS classes */
  className?: string;
  /** Delay in ms before showing theme preview when hovering (default: 150ms) */
  previewDelay?: number;
  /** Whether to disable the preview functionality (default: false) */
  disablePreview?: boolean;
}

/**
 * A specialized ThemeSwitcher component that uses Radix UI DropdownMenu
 * for better positioning and viewport awareness.
 *
 * Focuses ONLY on theme selection (color palettes).
 */
export const ThemeSelect: React.FC<ThemeSelectProps> = ({
  className,
  previewDelay = 150,
  disablePreview = false,
}) => {
  const { theme, setTheme, previewTheme } = useTheme();

  // Available themes (color palettes)
  const themes: ThemeOption[] = themeData.getThemeOptions();

  // Use theme preview hook to manage preview behavior
  const { handleMouseEnter, handleMouseLeave, handleClose } = useThemePreview(
    previewTheme,
    { delay: previewDelay, disabled: disablePreview },
  );

  const content = (
    <DropdownMenu onOpenChange={(open) => !open && handleClose()}>
      <DropdownMenuTrigger
        aria-label="Select a theme"
        className={cn(
          "flex items-center gap-2 text-sm px-2.5 cursor-pointer flex-shrink-0",
          transitions.colors,
          themeComponentStyles.getContainerClasses(),
          "hover:bg-gray-100 dark:hover:bg-gray-700/50",
          themeComponentStyles.getControlClasses(),
        )}
      >
        {/* Color indicator for current theme */}
        <div
          className={cn(
            "h-5 w-5 rounded-full ring-1 ring-white/50 shadow-sm",
            themeColors.getColorIndicatorClass({ themeId: theme }),
          )}
        />
        <svg
          aria-hidden="true"
          className="h-4 w-4 transition-transform-standard hidden sm:block"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <title>Toggle dropdown</title>
          <path
            d="M19 9l-7 7-7-7"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
          />
        </svg>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="min-w-[12rem] p-1"
        sideOffset={4}
      >
        <DropdownMenuLabel>Theme</DropdownMenuLabel>

        {themes.map((schemeOption) => {
          const isActive = theme === schemeOption.id;

          return (
            <DropdownMenuItem
              className={cn(
                "flex w-full cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none",
                transitions.colors,
                themeSchemaUtils.getSchemeClasses({
                  isActive,
                  themeId: schemeOption.id,
                }),
              )}
              key={schemeOption.id}
              onMouseEnter={() => handleMouseEnter(schemeOption.id)}
              onMouseLeave={handleMouseLeave}
              onSelect={() => setTheme(schemeOption.id)}
            >
              {/* Render the color indicator with the scheme's actual color */}
              <div className="relative">
                <div
                  className={cn(
                    "h-5 w-5 rounded-full",
                    themeColors.getColorIndicatorClass({
                      themeId: schemeOption.id,
                    }),
                  )}
                />
                {isActive && (
                  <div
                    className={cn(
                      "absolute inset-0 ring-2 ring-offset-1 rounded-full",
                      themeSchemaUtils.getThemeRingClass({
                        themeId: schemeOption.id,
                      }),
                    )}
                  />
                )}
              </div>
              <span className="font-medium">{schemeOption.label}</span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return className ? <div className={className}>{content}</div> : content;
};
